// frontend/src/components/Pages/DashboardAdmin.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button, Form, InputGroup, Row, Col } from "react-bootstrap";
import axios from "axios";
import authService from '../../services/authService';
import { useNavigate, Link } from 'react-router-dom';
import './DashboardAdmin.css'; // Ensure this CSS file exists and is correctly named

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
  </svg>
);
const DeleteIcon = () => ( 
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash3-fill" viewBox="0 0 16 16">
        <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5"/>
    </svg>
);

// Dropdown options
const statusOptions = [
  "1st_proof", "2nd_proof", "3rd_proof", "Geodesy", "Boundary_section",
  "GIS", "Digital_Carto", "Director_office", "DPCO", "CPT", "LPO",
  "Pending", "In Progress", "Completed", "Submitted"
];
const partyUnitOptions = [
  "1Party", "2Party", "3Party", "4Party", "5Party",
  "1Carto", "2Carto", "Photo_office"
];
const seasonOptions = [
  "2022-2023", "2023-2024", "2024-2025", "2025-2026",
  "2026-2027", "2027-2028", "2028-2029", "2029-2030",
  "2030-2031", "2031-2032"
];
const workingStatusOptions = [
  "In progress", "Pending", "Submitted", "Completed"
];

function DashboardAdmin() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [addFormData, setAddFormData] = useState({});
  const [editFormData, setEditFormData] = useState({});
  const [editingRowId, setEditingRowId] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPartyUnitFilter, setSelectedPartyUnitFilter] = useState('');
  const [selectedSeasonFilter, setSelectedSeasonFilter] = useState('');

  const navigate = useNavigate();
  
  const columnStructure = useMemo(() => [
    { label: "SL", category: "General", fields: [{ name: "sl", label: "SL" }] },
    { label: "Upazila sheet/Sheet number", category: "General", fields: [{ name: "upazila_sheet", label: "Upazila sheet/Sheet number" }] },
    { label: "Party/Unit", category: "General", fields: [{ name: "party_unit", label: "Party/Unit" }] },
    { label: "Surveyor name", category: "General", fields: [{ name: "surveyor_name", label: "Surveyor name" }] },
    { label: "Status", category: "General", fields: [{ name: "status", label: "Status" }] },
    { label: "Season", category: "General", fields: [{ name: "season", label: "Season" }] },
    { label: "Working_Status", category: "General", fields: [{ name: "working_status", label: "Working Status" }] },
    { label: "GIS Preparation for 1st Proof", category: "Proof Stages", fields: [{ name: `gis1_receive_date`, label: `Receive Date` }, { name: `gis1_operator_name`, label: `Operator name` }, { name: `gis1_send_date`, label: `Send Date` }] },
    { label: "Cartrographic Preparation for 1st Proof", category: "Proof Stages", fields: [{ name: `carto1_receive_date`, label: `Receive Date` }, { name: `carto1_operator_name`, label: `Operator name` }, { name: `carto1_send_date`, label: `Send Date` }] },
    { label: "1st_proof correction by party", category: "Proof Stages", fields: [{ name: `corr1_receive_date`, label: `Receive Date` }, { name: `corr1_operator_name`, label: `Operator name` }, { name: `corr1_send_date`, label: `Send Date` }] },
    { label: "GIS Preparation for 2nd Proof", category: "Proof Stages", fields: [{ name: `gis2_receive_date`, label: `Receive Date` }, { name: `gis2_operator_name`, label: `Operator name` }, { name: `gis2_send_date`, label: `Send Date` }] },
    { label: "Cartrographic Preparation for 2nd Proof", category: "Proof Stages", fields: [{ name: `carto2_receive_date`, label: `Receive Date` }, { name: `carto2_operator_name`, label: `Operator name` }, { name: `carto2_send_date`, label: `Send Date` }] },
    { label: "2nd_proof correction by party", category: "Proof Stages", fields: [{ name: `corr2_receive_date`, label: `Receive Date` }, { name: `corr2_operator_name`, label: `Operator name` }, { name: `corr2_send_date`, label: `Send Date` }] },
    { label: "GIS Preparation for 3rd Proof", category: "Proof Stages", fields: [{ name: `gis3_receive_date`, label: `Receive Date` }, { name: `gis3_operator_name`, label: `Operator name` }, { name: `gis3_send_date`, label: `Send Date` }] },
    { label: "Cartrographic Preparation for 3rd Proof", category: "Proof Stages", fields: [{ name: `carto3_receive_date`, label: `Receive Date` }, { name: `carto3_operator_name`, label: `Operator name` }, { name: `carto3_send_date`, label: `Send Date` }] },
    { label: "3rd_proof correction by party", category: "Proof Stages", fields: [{ name: `corr3_receive_date`, label: `Receive Date` }, { name: `corr3_operator_name`, label: `Operator name` }, { name: `corr3_send_date`, label: `Send Date` }] },
    { label: "Boundary Checking", category: "Boundary/Geodesy", fields: [{ name: "boundary_receive_date", label: "Receive Date" }, { name: "boundary_operator_name", label: "Operator name" }, { name: "boundary_send_date", label: "Send Date" }] },
    { label: "Geodesy Checking", category: "Boundary/Geodesy", fields: [{ name: "geodesy_receive_date", label: "Receive Date" }, { name: "geodesy_operator_name", label: "Operator name" }, { name: "geodesy_send_date", label: "Send Date" }] },
    { label: "sheet coreection by D.Dev", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "ddev_receive_date", label: "Receive Date" }, { name: "ddev_corrected_by", label: "Corrected by" }, { name: "ddev_send_date", label: "Send Date" }, { name: "ddev_send_to", label: "Send to" }] },
    { label: "GIs preparation from D.Dev", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "ddev_gis_receive_date", label: "Receive Date" }, { name: "ddev_gis_operator_name", label: "Operator name" }, { name: "ddev_gis_send_date", label: "Send Date" }] },
    { label: "cartrophic preparation from D.Dev", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "ddev_carto_receive_date", label: "Receive Date" }, { name: "ddev_carto_operator_name", label: "Operator name" }, { name: "ddev_carto_send_date", label: "Send Date" }] },
    { label: "Director preparation from D.Dev", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "ddev_director_receive_date", label: "Receive Date" }, { name: "ddev_director_corrected_by", label: "corrected By" }, { name: "ddev_director_send_date", label: "Send Date to DPCO" }] },
    { label: "sheet coreection by DPCO", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_receive_date", label: "Receive Date" }, { name: "dpco_corrected_by", label: "Corrected by" }, { name: "dpco_send_date", label: "Send Date" }, { name: "dpco_send_to", label: "Send to" }] },
    { label: "GIs preparation from DPCO", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_gis_receive_date", label: "Receive Date" }, { name: "dpco_gis_operator_name", label: "Operator name" }, { name: "dpco_gis_send_date", label: "Send Date" }] },
    { label: "cartrophic preparation from DPCO", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_carto_receive_date", label: "Receive Date" }, { name: "dpco_carto_operator_name", label: "Operator name" }, { name: "dpco_carto_send_date", label: "Send Date" }] },
    { label: "Preparation from DPCO", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_prep_receive_date", label: "Receive Date" }, { name: "dpco_prep_operator_name", label: "Operator name" }, { name: "dpco_prep_send_date", label: "Send Date to Cartrographic" }] },
    { label: "DPCO to GIS", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_to_gis_receive_date", label: "Receive Date" }, { name: "dpco_to_gis_operator_name", label: "Operator name" }, { name: "dpco_to_gis_send_date", label: "Send Date" }] },
    { label: "DPCO to Carto", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_to_carto_receive_date", label: "Receive Date" }, { name: "dpco_to_carto_operator_name", label: "Operator name" }, { name: "dpco_to_carto_send_date", label: "Send Date" }] },
    { label: "DPCO to CPT", category: "DDEV/DPCO/CPT/LPO", fields: [
        { name: "dpco_to_cpt_receive_date", label: "Receive Date" }, 
        { name: "dpco_to_cpt_corrected_by", label: "corrected By" }, 
        { name: "dpco_to_cpt_send_date", label: "Send Date to LPO" }
      ] 
    },
    { label: "Print by LPO(Date)", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "lpo_print_date", label: "Print by LPO (Date)" }] }
  ], []);

  const allFields = useMemo(() => columnStructure.flatMap(group => group.fields), [columnStructure]);
  const fieldsForAddModal = useMemo(() => {
    return allFields.filter(field => field.name !== 'sl').map(field => {
        if (field.name === 'status') { return { ...field, type: 'select', options: statusOptions }; }
        if (field.name === 'party_unit') { return { ...field, type: 'select', options: partyUnitOptions }; }
        if (field.name === 'season') { return { ...field, type: 'select', options: seasonOptions }; }
        if (field.name === 'working_status') { return { ...field, type: 'select', options: workingStatusOptions }; }
        return field;
    });
  }, [allFields]);

  //const API_BASE_URL = "http://localhost:5000/api/2party";
    const API_BASE_URL = "https://sheettrackernode.sobalex.com/api/2party";

  const getCategoryClass = (category) => { /* ... same as before ... */ 
    switch (category) {
      case "General": return "general-column-group";
      case "Proof Stages": return "proof-stages-column-group";
      case "Boundary/Geodesy": return "geo-column-group";
      case "DDEV/DPCO/CPT/LPO": return "ddev-dpco-column-group";
      default: return "";
    }
  };

  const fetchData = useCallback(async (editedRowIdToCheck = null) => {
    const currentUser = authService.getCurrentUser(); 
    if (!currentUser || !currentUser.token) { navigate('/login'); return; }
    if (currentUser.user.role !== 'admin') {
      setError('Access Denied: You do not have permission to view this page.');
      setLoading(false); return;
    }
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/records`, { headers: { Authorization: `Bearer ${currentUser.token}` } });
      if (res.data.status === "success") {
        const newData = Array.isArray(res.data.data) ? res.data.data : [];
        setTableData(newData);
        if (editedRowIdToCheck !== null) {
            const updatedRow = newData.find(r => r.id === editedRowIdToCheck);
            console.log(`DEBUG (fetchData Admin): Fetched data for edited row ID ${editedRowIdToCheck}:`, updatedRow);
        }
      } else { setError(res.data.message || "Failed to fetch data."); setTableData([]); }
    } catch (err) {
        console.error("Fetch error (Admin):", err);
        if (err.response) {
            const errMsg = err.response.data.message || 'Error fetching data.';
            setError(`Server Error ${err.response.status}: ${errMsg}`);
            if (err.response.status === 401 || err.response.status === 403) { authService.logout(); navigate('/login');}
        } else if (err.request) { setError('No server response.');
        } else { setError('Error setting up request.');}
        setTableData([]);
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    const currentUserOnMount = authService.getCurrentUser();
    if (!currentUserOnMount || !currentUserOnMount.token) { navigate('/login'); return; }
    if (currentUserOnMount.user.role !== 'admin') {
        setError('Access Denied.'); setLoading(false); return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleAddChange = (e) => { setAddFormData({ ...addFormData, [e.target.name]: e.target.value }); };
  
  const handleAddSubmit = async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || !currentUser.token || currentUser.user.role !== 'admin') {
        alert("Permission Denied or Auth error."); navigate('/login'); return;
    }
    
    if (Object.keys(addFormData).length === 0 || !addFormData.upazila_sheet) { 
        alert("Please fill in at least the 'Upazila sheet/Sheet number' field.");
        setError("Cannot submit an empty or incomplete record.");
        return;
    }

    console.log("DEBUG: Data being sent for ADD:", JSON.stringify(addFormData, null, 2)); 

    setLoading(true); setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/records`, addFormData, { headers: { "Content-Type": "application/json", Authorization: `Bearer ${currentUser.token}` }});
      console.log("DEBUG: Add API Response:", response);
      if (response.data.status === "success") {
        alert("✅ Data added successfully!"); setShowAddModal(false); setAddFormData({}); fetchData();
      } else { 
        const errMsg = response.data.message || "Backend error while adding data."; 
        alert(`❌ Add failed: ${errMsg}`); 
        setError(errMsg); 
      }
    } catch (err) {
        console.error("Add Submit Error (Admin):", err.response || err);
        let submitErrorMsg = 'Server error. Could not submit data.';
        if (err.response) { 
            submitErrorMsg = err.response.data.message || `Server Error ${err.response.status}.`; 
            if (err.response.status === 401 || err.response.status === 403) { authService.logout(); navigate('/login');}
            else if (err.response.status === 400 ) { 
                submitErrorMsg = `Error: ${err.response.data.message || "Bad Request. Please check your input."}`;
            }
        }
        else if (err.request) { submitErrorMsg = 'No server response for add.'; }
        else { submitErrorMsg = 'Error setting up add request.';}
        alert(`⚠️ ${submitErrorMsg}`); setError(submitErrorMsg);
    } finally { setLoading(false); }
  };

  const handleEditClick = (rowData) => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || currentUser.user.role !== 'admin') {
        alert("Permission Denied. Only admins can edit records here.");
        setError("Permission Denied."); return;
    }
    const initialEditData = {};
    allFields.forEach(field => { 
        initialEditData[field.name] = rowData[field.name] === null || rowData[field.name] === undefined ? '' : rowData[field.name];
        if (field.name.includes("date") && initialEditData[field.name]) {
            const date = new Date(initialEditData[field.name]);
            if (!isNaN(date.getTime())) { initialEditData[field.name] = date.toISOString().split('T')[0]; }
        }
    });
    setEditFormData(initialEditData); setEditingRowId(rowData.id); setShowEditModal(true); setError('');
  };
  const handleEditChange = (e) => { setEditFormData({ ...editFormData, [e.target.name]: e.target.value }); };
  
  const handleEditSubmit = async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || !currentUser.token || currentUser.user.role !== 'admin') {
        alert("Permission Denied or Auth error."); navigate('/login'); return;
    }
    if (!editingRowId) { alert("No record selected."); return; }
    
    let dataToSubmit = { ...editFormData }; 
    Object.keys(dataToSubmit).forEach(fieldName => {
        if (fieldName.includes('_date') && dataToSubmit[fieldName] === '') {
            dataToSubmit[fieldName] = null;
        }
    });
    console.log("DEBUG: Data being sent for EDIT (ID:", editingRowId, "):", JSON.stringify(dataToSubmit, null, 2)); 

    setLoading(true); setError('');
    try {
      const response = await axios.put(`${API_BASE_URL}/records/${editingRowId}`, dataToSubmit, { headers: { "Content-Type": "application/json", Authorization: `Bearer ${currentUser.token}` }});
      console.log("DEBUG: Edit API Response:", response);
      if (response.data.status === "success") {
        alert("✅ Record updated!");
        const currentEditingId = editingRowId; 
        setShowEditModal(false); setEditingRowId(null); setEditFormData({});
        fetchData(currentEditingId); 
      } else { 
        const errMsg = response.data.message || "Update error from backend."; 
        alert(`❌ Update failed: ${errMsg}`); 
        setError(errMsg); 
      }
    } catch (err) {
        console.error("Edit Submit Error (Admin):", err.response || err);
        let updateErrorMsg = 'Server error. Could not update record.';
        if (err.response) { 
            updateErrorMsg = err.response.data.message || `Server Error ${err.response.status}.`; 
            if (err.response.status === 401 || err.response.status === 403) { authService.logout(); navigate('/login');}
            else if (err.response.status === 500 ) { 
                 updateErrorMsg = `Server Error: ${err.response.data.message || "An internal server error occurred during update."}`;
            }
        }
        else if (err.request) { updateErrorMsg = 'No server response for update.'; }
        else { updateErrorMsg = 'Error setting up update request.';}
        alert(`⚠️ ${updateErrorMsg}`); setError(updateErrorMsg);
    } finally { setLoading(false); }
  };

  const handleDeleteClick = async (recordId, recordSL) => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || currentUser.user.role !== 'admin') {
        alert("Permission Denied. Only admins can delete records.");
        return;
    }

    if (window.confirm(`Are you sure you want to delete record SL: ${recordSL} (ID: ${recordId})? This action cannot be undone.`)) {
        setLoading(true);
        setError('');
        try {
            console.log(`DEBUG: Attempting to delete record ID: ${recordId} with token: ${currentUser.token ? "Present" : "MISSING"}`);
            const response = await axios.delete(`${API_BASE_URL}/records/${recordId}`, {
                headers: { Authorization: `Bearer ${currentUser.token}` }
            });
            console.log("DEBUG: Delete API Response:", response);
            if (response.data.status === "success") {
                alert("🗑️ Record deleted successfully!");
                fetchData(); 
            } else {
                const errMsg = response.data.message || "Delete error from backend.";
                alert(`❌ Delete failed: ${errMsg}`);
                setError(errMsg);
            }
        } catch (err) {
            console.error("Delete error (Admin):", err);
            let deleteErrorMsg = 'Server error. Could not delete record.';
            if (err.response) {
                console.error("Delete Error response data:", err.response.data);
                console.error("Delete Error response status:", err.response.status);
                deleteErrorMsg = err.response.data.message || `Server Error ${err.response.status} during delete.`;
                if (err.response.status === 401 || err.response.status === 403) {
                    authService.logout(); navigate('/login');
                }
            } else if (err.request) {
                console.error("Delete No response received:", err.request);
                deleteErrorMsg = 'No server response for delete.';
            } else {
                console.error('Delete Error setting up request:', err.message);
                deleteErrorMsg = 'Error setting up delete request.';
            }
            alert(`⚠️ ${deleteErrorMsg}`);
            setError(deleteErrorMsg);
        } finally {
            setLoading(false);
        }
    }
  };

  const getEditableFieldsForModal = useCallback(() => {
      return allFields.map(field => {
        if (field.name === 'status') { return { ...field, type: 'select', options: statusOptions }; }
        if (field.name === 'party_unit') { return { ...field, type: 'select', options: partyUnitOptions }; }
        if (field.name === 'season') { return { ...field, type: 'select', options: seasonOptions }; }
        if (field.name === 'working_status') { return { ...field, type: 'select', options: workingStatusOptions }; }
        return { ...field }; 
      });
  }, [allFields]);

  const filteredTableData = useMemo(() => { /* ... same as before ... */ 
    let data = tableData;
    if (selectedPartyUnitFilter) { data = data.filter(row => row.party_unit === selectedPartyUnitFilter); }
    if (selectedSeasonFilter) { data = data.filter(row => row.season === selectedSeasonFilter); }
    if (!searchTerm.trim()) { return data; }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return data.filter(row => {
      return allFields.some(field => {
        const value = row[field.name];
        return value !== null && value !== undefined && String(value).toLowerCase().includes(lowercasedSearchTerm);
      });
    });
  }, [tableData, searchTerm, selectedPartyUnitFilter, selectedSeasonFilter, allFields]);

  const statusSummary = useMemo(() => { /* ... same as before ... */ 
    let dataToSummarize = tableData;
    if (selectedPartyUnitFilter) { dataToSummarize = dataToSummarize.filter(row => row.party_unit === selectedPartyUnitFilter); }
    if (selectedSeasonFilter) { dataToSummarize = dataToSummarize.filter(row => row.season === selectedSeasonFilter); }
    const counts = { allSheets: dataToSummarize.length };
    statusOptions.forEach(status => counts[status] = 0); 
    dataToSummarize.forEach(row => { if (row.status && statusOptions.includes(row.status)) { counts[row.status]++; } });
    return counts;
  }, [tableData, selectedPartyUnitFilter, selectedSeasonFilter]);

  const getRowClassByWorkingStatus = (workingStatus) => { /* ... same as before ... */ 
    if (!workingStatus) return '';
    const status = String(workingStatus).trim().toLowerCase();
    switch (status) {
      case 'in progress': return 'row-in-progress';
      case 'pending': return 'row-pending';
      case 'submitted': return 'row-submitted';
      case 'completed': return 'row-completed';
      default: return '';
    }
  };

  if (loading && tableData.length === 0 && !error) { /* ... same as before ... */ 
    return ( <div className="container mt-5 text-center"> <div className="spinner-border text-primary" role="status"> <span className="sr-only">Loading...</span></div><p>Loading...</p></div>);
  }
  if (error) { /* ... same as before ... */ 
    return (<div className="container mt-5"><div className="alert alert-danger text-center"><h4>Error</h4><p>{error}</p><Link to="/home" className="btn btn-info mt-2">Home</Link></div></div>);
  }

  return (
    <div className="container-fluid mt-4 dashboard-admin-container">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h2 className="dashboard-admin-header mb-0 mr-3">Admin Dashboard - Full Data Management</h2>
        <Button variant="primary" onClick={() => { setError(''); setAddFormData({}); setShowAddModal(true); }}>Add New Record</Button>
      </div>
      
      {/* Filters and Summary sections remain the same */}
      <Row className="mb-3 align-items-end">
        <Col md={4} lg={3}><Form.Group controlId="partyUnitFilter"><Form.Label className="mb-1"><small>Filter by Party/Unit:</small></Form.Label><Form.Select size="sm" value={selectedPartyUnitFilter} onChange={(e) => setSelectedPartyUnitFilter(e.target.value)} ><option value="">All Party/Units</option>{partyUnitOptions.map(option => (<option key={option} value={option}>{option}</option>))}</Form.Select></Form.Group></Col>
        <Col md={4} lg={3}><Form.Group controlId="seasonFilter"><Form.Label className="mb-1"><small>Filter by Season:</small></Form.Label><Form.Select size="sm" value={selectedSeasonFilter} onChange={(e) => setSelectedSeasonFilter(e.target.value)} ><option value="">All Seasons</option>{seasonOptions.map(option => (<option key={option} value={option}>{option}</option>))}</Form.Select></Form.Group></Col>
        <Col md={4} lg={4}><Form.Group controlId="searchTerm"><Form.Label className="mb-1"><small>Search Table:</small></Form.Label><InputGroup size="sm"><Form.Control type="text" placeholder="Search all columns..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /><InputGroup.Text><SearchIcon /></InputGroup.Text></InputGroup></Form.Group></Col>
      </Row>
      <div className="mb-3 p-3 border rounded bg-light shadow-sm">
        <h6>Status Summary for: <strong className="text-primary">{selectedPartyUnitFilter || 'All Party/Units'}{selectedSeasonFilter && ` (Season: ${selectedSeasonFilter})`}</strong></h6>
        <Row xs={1} sm={2} md={3} lg={4} className="g-2">
            <Col><strong>Total Sheets: {statusSummary.allSheets}</strong></Col>
            {Object.entries(statusSummary).filter(([key]) => key !== 'allSheets' && statusSummary[key] > 0).map(([status, count]) => (<Col key={status}>{status.replace(/_/g, ' ')}: <span className="font-weight-bold">{count}</span></Col>))}
        </Row>
        {Object.values(statusSummary).filter((val, index) => index > 0 && val > 0).length === 0 && statusSummary.allSheets > 0 && (<Row><Col><small className="text-muted mt-1 d-block">(No items in specific statuses)</small></Col></Row>)}
        {statusSummary.allSheets === 0 && (<Row><Col><small className="text-muted mt-1 d-block">(No sheets for this selection)</small></Col></Row>)}
      </div>

      {/* Add Data Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="xl" scrollable>
        <Modal.Header><Modal.Title>Add New Sheet Record</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            {fieldsForAddModal.map(field => (
              <Form.Group key={`add-${field.name}`} className="mb-2 row">
                <Form.Label column sm="4" className="text-sm-right">{field.label}:</Form.Label>
                <div className="col-sm-8">
                  {field.type === 'select' ? (
                    <Form.Select name={field.name} value={addFormData[field.name] || ""} onChange={handleAddChange}>
                      <option value="">-- Select {field.label} --</option>
                      {field.options.map(option => (<option key={option} value={option}>{option}</option>))}
                    </Form.Select>
                  ) : ( <Form.Control type={field.name.includes("date") ? "date" : "text"} name={field.name} value={addFormData[field.name] || ""} onChange={handleAddChange} /> )}
                </div>
              </Form.Group>
            ))}
            <Button className="mt-3 w-100" variant="success" onClick={handleAddSubmit} disabled={loading}>{loading && !showEditModal ? 'Submitting...' : 'Submit Record'}</Button>
            <Button variant="secondary" className="mt-2 w-100" onClick={() => setShowAddModal(false)}>Cancel</Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Data Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="xl" scrollable>
        <Modal.Header><Modal.Title>Edit Record (ID: {editingRowId})</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            {getEditableFieldsForModal().map(field => ( 
              <Form.Group key={`edit-${field.name}`} className="mb-2 row">
                <Form.Label column sm={4} className="text-sm-right">{field.label}:</Form.Label>
                <div className="col-sm-8">
                  {field.type === 'select' ? (
                    <Form.Select name={field.name} value={editFormData[field.name] || ""} onChange={handleEditChange}>
                      <option value="">-- Select {field.label} --</option>
                      {field.options.map(optionValue => (<option key={optionValue} value={optionValue}>{optionValue}</option>))}
                    </Form.Select>
                  ) : ( <Form.Control type={field.type || (field.name.includes("date") ? "date" : "text")} name={field.name} value={editFormData[field.name] || ""} onChange={handleEditChange}/> )}
                </div>
              </Form.Group>
            ))}
            <Button className="mt-3 w-100" variant="success" onClick={handleEditSubmit} disabled={loading}>{loading && showEditModal ? 'Saving...' : 'Save Changes'}</Button>
            <Button variant="secondary" className="mt-2 w-100" onClick={() => setShowEditModal(false)}>Cancel</Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Table for displaying data */}
      <div className="table-responsive">
        <table className="table table-bordered table-sm table-hover">
          <thead>
            <tr>
              {columnStructure.map((group, index) => {
                if (group.fields.length === 1) { return ( <th key={`main-${index}`} rowSpan={2} className={`text-center align-middle main-column-header column-group-${index} border-right-group`} > {group.label} </th> ); }
                else { return ( <th key={`main-${index}`} colSpan={group.fields.length} className={`text-center align-middle main-column-header column-group-${index} border-right-group`} > {group.label} </th> ); }
              })}
              <th rowSpan={2} className="text-center align-middle main-column-header">Actions</th>
            </tr>
            <tr>
              {columnStructure.flatMap((group, groupIndex) => {
                if (group.fields.length > 1) {
                  return group.fields.map((field, fieldIndex) => {
                    const isLastSubFieldInGroup = fieldIndex === group.fields.length - 1;
                    const headerClasses = `${getCategoryClass(group.category)} sub-column-header ${isLastSubFieldInGroup ? 'border-right-group-header' : ''}`.trim();
                    return ( <th key={`sub-${groupIndex}-${fieldIndex}`} className={headerClasses}> {field.label} </th> );
                  });
                } return null; 
              })}
              <th className="sub-column-header text-center">Edit / Delete</th>
            </tr>
          </thead>
          <tbody>
            {filteredTableData.length > 0 ? (
              filteredTableData.map((row, rowIndex) => {
                const rowClass = getRowClassByWorkingStatus(row.working_status);
                return (
                <tr key={row.id || rowIndex} className={rowClass}>
                  {allFields.map((field) => {
                      const groupForField = columnStructure.find(g => g.fields.some(f => f.name === field.name));
                      const isLastFieldInMainGroup = groupForField && groupForField.fields[groupForField.fields.length - 1].name === field.name;
                      const categoryClass = groupForField ? getCategoryClass(groupForField.category) : '';
                      const cellClasses = `${categoryClass} ${isLastFieldInMainGroup || (groupForField && groupForField.fields.length === 1) ? 'border-right-group' : ''}`.trim();
                      let cellValue = row[field.name];
                      if (field.name.includes("date") && cellValue) {
                        const date = new Date(cellValue);
                        if (!isNaN(date.getTime())) {
                            const [year, month, day] = cellValue.split('T')[0].split('-');
                            if (year && month && day && year.length === 4) { cellValue = `${year}-${month}-${day}`; }
                            else { cellValue = date.toLocaleDateString('en-CA'); }
                        } else { cellValue = cellValue || "N/A"; }
                      }
                    return ( <td key={field.name} className={cellClasses}> {cellValue === null || cellValue === undefined ? '' : cellValue} </td> );
                  })}
                  {/* Actions cell with Edit and Delete buttons */}
                  <td className="text-center align-middle">
                    <Button variant="outline-primary" size="sm" className="me-2 mb-1 mb-md-0" onClick={() => handleEditClick(row)}>
                      Edit
                    </Button>
                    <Button variant="outline-danger" size="sm" className="btn-delete-custom" onClick={() => handleDeleteClick(row.id, row.sl)}>
                      <DeleteIcon /> Delete
                    </Button>
                  </td>
                </tr>
              );
            })
            ) : ( 
              <tr><td colSpan={allFields.length + 1} className="text-center py-4"> {/* +1 for Actions column */}
                {searchTerm || selectedPartyUnitFilter || selectedSeasonFilter ? 'No matching records found.' : 'No data available.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DashboardAdmin;
